import { invalidateAllPrivateCaches } from './private-data-cache';

/**
 * Backend API client - Proxied through Next.js server
 * All requests go through /api/backend/* which handles authentication server-side
 * This is necessary because NextAuth JWT tokens are HttpOnly and can't be accessed from client-side
 */

/**
 * Fetch data from backend with authentication (via server-side proxy)
 * This function can be called from client components
 * Authentication is handled server-side by the /api/backend proxy
 */
// One-shot session refresh used by the 401 handler. Returns true if a fresh token was issued.
// De-duped so a burst of parallel 401s only triggers a single refresh round-trip.
let inflightRefresh: Promise<boolean> | null = null;
async function tryRefreshSession(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
      return res.ok;
    } catch {
      return false;
    } finally {
      // allow a new refresh attempt on the next 401 wave
      setTimeout(() => { inflightRefresh = null; }, 0);
    }
  })();
  return inflightRefresh;
}

export async function fetchWithAuth(
  endpoint: string,
  options: RequestInit = {},
  _isRetry = false
): Promise<Response> {
  // Clean endpoint (remove leading /api/ if present)
  const cleanEndpoint = endpoint.replace(/^\/api\//, '');

  // Route through our server-side proxy instead of directly to backend
  const response = await fetch(`/api/backend/${cleanEndpoint}`, {
    ...options,
    credentials: 'include', // Important: include session cookie
  });

  // Handle 401 Unauthorized - session expired.
  if (response.status === 401) {
    // Try a one-shot silent token refresh, then retry the original request once.
    if (!_isRetry) {
      const refreshed = await tryRefreshSession();
      if (refreshed) {
        return fetchWithAuth(endpoint, options, true);
      }
    }
    // Refresh failed (or this was already the retry): send the user to the landing page where the
    // login modal lives. There is NO /login route — redirecting there 404s. Don't loop on the
    // landing page itself.
    if (typeof window !== 'undefined' && window.location.pathname !== '/') {
      window.location.href = '/?auth=login&reason=expired';
    }
    throw new Error('Session expired. Please log in again.');
  }

  // Every other non-ok status (incl. 502/503 from the payment provider) is a plain API error —
  // never a session expiry. Only the 401 branch above may log the user out.
  if (!response.ok) {
    let error;
    let code: string | undefined;
    let retryAfter: number | undefined;
    // Read the body ONCE: calling .text() after a failed .json() throws ("body already used"),
    // which used to replace the real error (and its status) with a TypeError.
    const raw = await response.text().catch(() => '');
    try {
      const errorData = JSON.parse(raw);
      error = errorData.error || errorData.message || 'API error';
      code = typeof errorData.code === 'string' ? errorData.code : undefined;
      retryAfter = parseRetryAfter(errorData.retryAfter);
    } catch {
      error = raw || 'API error';
    }
    retryAfter ??= parseRetryAfter(response.headers.get('retry-after'));
    // Attach the HTTP status + machine code so callers can branch on them
    // (e.g. treat a 404 / *_NOT_FOUND as "deleted"). Extra props are additive,
    // so callers that only read `.message` are unaffected.
    const err = new Error(error) as ApiError;
    err.status = response.status;
    err.code = code;
    if (retryAfter !== undefined) err.retryAfter = retryAfter;
    throw err;
  }

  return response;
}

/** Seconds to wait, from a body `retryAfter` or a Retry-After header (delta-seconds or HTTP date). */
function parseRetryAfter(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return seconds > 0 ? Math.ceil(seconds) : undefined;
  if (typeof value !== 'string') return undefined;
  const at = Date.parse(value);
  if (!Number.isFinite(at)) return undefined;
  const delta = Math.ceil((at - Date.now()) / 1000);
  return delta > 0 ? delta : undefined;
}

/** Error thrown by {@link fetchWithAuth} for non-ok responses, carrying the HTTP status + machine code. */
export interface ApiError extends Error {
  status?: number;
  code?: string;
  /** Seconds the server asked us to wait before retrying (rate limited / provider busy). */
  retryAfter?: number;
}

/**
 * Get authenticated image URL from backend (proxied through our API)
 */
export function getAuthenticatedImageUrl(path: string): string {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  
  // Route through our proxy for consistent authentication
  // Don't encode the entire path - the backend expects slashes in the path
  return `/api/backend/images/serve/${cleanPath}`;
}

/**
 * Upload image to backend
 */
export async function uploadImage(file: File) {
  const formData = new FormData();
  formData.append('image', file);

  try {
    const response = await fetchWithAuth('images/upload', {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();
    if (data?.success !== false) invalidateAllPrivateCaches('gallery:');
    return data;
  } catch (error: any) {
    return {
      success: false,
      message: error.message || 'Failed to upload image',
    };
  }
}

/**
 * Transform/Edit image
 * Endpoint: POST /api/images/edit
 * Note: The backend uses master prompt from AdminConfig if no custom prompt is provided
 */
export async function createEditJob(
  imageId: string,
  options?: {
    prompt?: string;
    negativePrompt?: string;
    outputFormat?: string;
    removeWatermark?: boolean;
  }
) {
  const body: any = {
    imageId, // Include imageId in the body
    outputFormat: options?.outputFormat || 'png',
    removeWatermark: options?.removeWatermark || false, // Default: with watermark (1 credit)
  };

  // Only include custom prompts if provided
  if (options?.prompt) {
    body.prompt = options.prompt;
  }
  if (options?.negativePrompt) {
    body.negativePrompt = options.negativePrompt;
  }

  const response = await fetchWithAuth('images/edit', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();

  // Track image generation with Facebook Pixel
  try {
    const { trackFacebookEvent } = await import('./facebook');
    trackFacebookEvent('GenerateContent', {
      content_type: 'image',
      content_category: 'image_edit',
      value: options?.removeWatermark ? 2 : 1,
      currency: 'USD',
    });
  } catch {}

  return data;
}

/**
 * Edit an existing image further (uses existing image as source)
 */
export async function editImageFurther(
  sourceImageId: string,
  options: {
    prompt: string;
    negativePrompt?: string;
    seed?: number;
    removeWatermark?: boolean;
  }
) {
  const body: any = {
    prompt: options.prompt,
    removeWatermark: options.removeWatermark || false,
  };

  if (options.negativePrompt) {
    body.negativePrompt = options.negativePrompt;
  }
  if (options.seed) {
    body.seed = options.seed;
  }

  const response = await fetchWithAuth(`images/edit-further/${sourceImageId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

    const data = await response.json();
    if (data?.success !== false) invalidateAllPrivateCaches('gallery:');
    return data;
}

/**
 * Get job status
 */
export async function getJobStatus(imageId: string) {
  const response = await fetchWithAuth(`images/${imageId}/status`);
  return response.json();
}

/**
 * Get single image by ID
 */
export async function getImageById(imageId: string) {
  const response = await fetchWithAuth(`images/${imageId}`);
  return response.json();
}

/**
 * Download the (watermarked) image as a Blob via the API. Streams through the
 * same-origin proxy so the browser reliably downloads it with a filename
 * (cross-origin `<a download>` on a CDN URL is ignored). Always watermarked.
 */
export async function downloadImageFile(imageId: string): Promise<Blob> {
  const response = await fetchWithAuth(`images/${imageId}/download`);
  return response.blob();
}

/**
 * Get user gallery
 */
export async function getGallery(page = 1, limit = 20) {
  const response = await fetchWithAuth(`images/gallery?page=${page}&limit=${limit}`);
  return response.json();
}

/**
 * Chat-design info for the delete-confirm warning: whether this image was
 * created in a chat and how many live designs that chat still has.
 */
export async function getImageChatDesigns(imageId: string) {
  const response = await fetchWithAuth(`images/${imageId}/chat-designs`);
  return response.json();
}

/**
 * Delete image from gallery. `scope: 'chat'` cascades the soft-delete to every
 * design created in the same chat; the default deletes only this image.
 */
export async function deleteImage(imageId: string, scope: 'single' | 'chat' = 'single') {
  const query = scope === 'chat' ? '?scope=chat' : '';
  const response = await fetchWithAuth(`images/${imageId}${query}`, {
    method: 'DELETE',
  });
  return response.json();
}

/**
 * Get credit balance
 */
export async function getCreditBalance() {
  const response = await fetchWithAuth('credits/balance');
  return response.json();
}

/**
 * Get transactions with filtering
 */
export async function getTransactions(options?: {
  type?:
    | 'credit_purchase'
    | 'image_edit'
    | 'video_generation'
    | 'refund'
    | 'bonus'
    | 'admin_add'
    | 'hold'
    | 'settle'
    | 'reconcile'
    | 'chat_turn'
    | 'agent_image'
    | 'agent_video'
    | 'agent_edit'
    | 'adjustment';
  category?: 'purchase' | 'usage' | 'refund' | 'adjustment';
  limit?: number;
  offset?: number;
  startDate?: string;
  endDate?: string;
}) {
  const params = new URLSearchParams();
  
  if (options?.type) params.append('type', options.type);
  if (options?.category) params.append('category', options.category);
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.offset) params.append('offset', options.offset.toString());
  if (options?.startDate) params.append('startDate', options.startDate);
  if (options?.endDate) params.append('endDate', options.endDate);
  
  const queryString = params.toString();
  const endpoint = queryString ? `transactions?${queryString}` : 'transactions';
  
  const response = await fetchWithAuth(endpoint);
  return response.json();
}

/**
 * Get transaction statistics
 */
export async function getTransactionStats() {
  const response = await fetchWithAuth('transactions/stats');
  return response.json();
}

/**
 * Get single transaction details
 */
export async function getTransaction(transactionId: string) {
  const response = await fetchWithAuth(`transactions/${transactionId}`);
  return response.json();
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use getTransactions() instead
 */
export async function getTransactionHistory(page = 1, limit = 20) {
  const offset = (page - 1) * limit;
  return getTransactions({ limit, offset });
}

/**
 * Get payment packages (public endpoint, but we still proxy it for consistency)
 */
export async function getPaymentPackages() {
  const response = await fetch('/api/backend/payment/packages', {
    credentials: 'include',
  });
  
  if (!response.ok) {
    let error;
    try {
      const errorData = await response.json();
      error = errorData.error || errorData.message || 'API error';
    } catch {
      error = await response.text();
    }
    throw new Error(error);
  }
  
  return response.json();
}

/**
 * Get billing summary for billing page
 */
export async function getBillingSummary() {
  const response = await fetchWithAuth('billing/summary');
  return response.json();
}

/**
 * Get all saved billing addresses
 */
export async function getBillingAddresses() {
  const response = await fetchWithAuth('billing-info');
  return response.json();
}

/**
 * Get default billing address
 */
export async function getDefaultBillingAddress() {
  const response = await fetchWithAuth('billing-info/default');
  return response.json();
}

/**
 * Save or update billing address
 */
export async function saveBillingAddress(data: {
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  address: {
    line1: string;
    line2?: string;
    city: string;
    state?: string;
    postalCode: string;
    country: string;
  };
}) {
  const response = await fetchWithAuth('billing-info', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

/**
 * Set default billing address
 */
export async function setDefaultBillingAddress(billingInfoId: string) {
  const response = await fetchWithAuth('billing-info/default', {
    method: 'PUT',
    body: JSON.stringify({ billingInfoId }),
  });
  return response.json();
}

/**
 * Delete billing address
 */
export async function deleteBillingAddress(billingInfoId: string) {
  const response = await fetchWithAuth(`billing-info/${billingInfoId}`, {
    method: 'DELETE',
  });
  return response.json();
}

/**
 * Create payment session for credit purchase
 */
export async function createPaymentSession(data: {
  packageId?: string;
  amount?: number;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  address: {
    line1: string;
    line2?: string;
    city: string;
    state?: string;
    postalCode: string;
    country: string;
  };
  metadata?: Record<string, any>;
}) {
  const response = await fetchWithAuth('payment/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  return response.json();
}

/**
 * Check payment status
 */
export async function checkPaymentStatus(sessionId: string) {
  const response = await fetchWithAuth(`payment/status/${sessionId}`);
  return response.json();
}

/**
 * Resolve the caller's own checkout session when the success redirect lost the session id
 * (e.g. checkout finished in a different browser context than the one that started it).
 */
export async function resolvePaymentSession(orderId?: string) {
  const qs = orderId ? `?orderId=${encodeURIComponent(orderId)}` : '';
  const response = await fetchWithAuth(`payment/session/resolve${qs}`);
  return response.json();
}

/**
 * Start a monthly membership (NxtPay subscription checkout).
 */
export async function createSubscriptionSession(data: {
  planId: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  address: {
    line1: string;
    line2?: string;
    city: string;
    state?: string;
    postalCode: string;
    country: string;
  };
}) {
  const response = await fetchWithAuth('payment/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

/** Current membership state (plan, status, renewal dates). */
export async function getSubscription() {
  const response = await fetchWithAuth('payment/subscription');
  return response.json();
}

/** Cancel the membership — stops future cycles; granted credits are kept. */
export async function cancelSubscription() {
  const response = await fetchWithAuth('payment/subscription/cancel', { method: 'POST' });
  return response.json();
}

/**
 * Get chat messages
 */
export async function getChatMessages(limit = 100) {
  const response = await fetchWithAuth(`chat/messages?limit=${limit}`);
  return response.json();
}

/**
 * Send a chat message or share an image
 */
export async function sendChatMessage(data: { message?: string; sharedImageId?: string }) {
  const response = await fetchWithAuth('chat/send', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return response.json();
}

/**
 * Report a chat message
 */
export async function reportChatMessage(messageId: string, reason: string, details?: string) {
  const response = await fetchWithAuth('chat/report', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ messageId, reason, details }),
  });
  return response.json();
}

/**
 * Get user's gallery for picker (completed images only).
 * Optional `type` filters to 'generated' (AI results/edits) or 'upload' (source photos).
 */
export async function getMyGalleryForPicker(type?: 'upload' | 'generated') {
  const qs = `images/gallery?limit=50${type ? `&type=${type}` : ''}`;
  const response = await fetchWithAuth(qs);
  return response.json();
}

/**
 * Update user email
 */
export async function updateEmail(newEmail: string, currentPassword: string) {
  const response = await fetchWithAuth('account/email', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newEmail, currentPassword }),
  });
  return response.json();
}

/**
 * Update user password (change password while logged in)
 */
export async function updatePassword(currentPassword: string, newPassword: string) {
  const response = await fetchWithAuth('auth/change-password', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  return response.json();
}

/**
 * Two-factor authentication (TOTP) — session-authenticated management for a
 * logged-in user, called from account settings. Enrollment is a two-step flow:
 * start() returns a QR + secret to scan, then confirm(code) turns it on.
 */
export async function start2FAEnrollment(): Promise<{
  success: boolean;
  qrDataUrl?: string;
  secret?: string;
  error?: string;
}> {
  const response = await fetchWithAuth('auth/2fa/enroll/start', { method: 'POST' });
  return response.json();
}

export async function confirm2FAEnrollment(code: string) {
  const response = await fetchWithAuth('auth/2fa/enroll/confirm', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  return response.json();
}

/** Disable 2FA — requires a current 6-digit code from the authenticator app. */
export async function disable2FA(code: string) {
  const response = await fetchWithAuth('auth/2fa/disable', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  return response.json();
}

/**
 * Password Reset Flow - Step 1: Request reset code
 */
export async function requestPasswordReset(email: string) {
  const response = await fetch('/api/backend/auth/request-password-reset', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return response.json();
}

/**
 * Password Reset Flow - Step 2: Verify reset code
 */
export async function verifyResetCode(email: string, code: string) {
  const response = await fetch('/api/backend/auth/verify-reset-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  return response.json();
}

/**
 * Password Reset Flow - Step 3: Reset password with token
 */
export async function resetPassword(resetToken: string, newPassword: string) {
  const response = await fetch('/api/backend/auth/reset-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resetToken, newPassword }),
  });
  return response.json();
}

/**
 * Email Change Flow - Step 1: Request email change with verification code
 */
export async function requestEmailChange(newEmail: string) {
  const response = await fetchWithAuth('auth/request-email-change', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newEmail }),
  });
  return response.json();
}

/**
 * Email Change Flow - Step 2: Verify code and change email
 */
export async function verifyEmailChange(code: string) {
  const response = await fetchWithAuth('auth/verify-email-change', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  return response.json();
}

/**
 * Validate a coupon code
 */
export async function validateCoupon(code: string, packageId?: string, amount?: number) {
  const body: any = { code };
  if (packageId) body.packageId = packageId;
  if (amount) body.amount = amount;

  const response = await fetchWithAuth('coupons/validate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return response.json();
}

/**
 * Redeem a voucher coupon for free credits
 */
export async function redeemVoucher(code: string) {
  const response = await fetchWithAuth('coupons/redeem', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  return response.json();
}

/**
 * Get coupon redemption history
 */
export async function getCouponHistory(limit = 50) {
  const response = await fetchWithAuth(`coupons/history?limit=${limit}`);
  return response.json();
}

/**
 * Video Generation API Functions
 */

/**
 * Get available LoRA styles for video generation (public endpoint)
 */
export async function getVideoLoras() {
  const response = await fetch('/api/backend/videos/loras', {
    method: 'GET',
    credentials: 'include',
  });
  return response.json();
}

/**
 * Get available duration presets for video generation (public endpoint)
 */
export async function getVideoDurations() {
  const response = await fetch('/api/backend/videos/durations', {
    method: 'GET',
    credentials: 'include',
  });
  return response.json();
}

/**
 * Generate video from image
 * Note: negativePrompt is NOT supported for video generation
 * loraId is required - LoRA selection is mandatory
 */
export async function generateVideo(imageId: string, options: {
  prompt: string;
  durationId: string;
  loraId: string;
  presetPromptId?: string;
  seed?: number;
  removeWatermark?: boolean;
}) {
  const body: Record<string, any> = { ...options };
  if (imageId) body.imageId = imageId;

  const response = await fetchWithAuth('videos/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json();

  // Track video generation with Facebook Pixel
  try {
    const { trackFacebookEvent } = await import('./facebook');
    trackFacebookEvent('GenerateContent', {
      content_type: 'video',
      content_category: 'video_generation',
      value: options.removeWatermark ? 40 : 20,
      currency: 'USD',
    });
  } catch {}

  return data;
}

/**
 * Get video status by ID
 */
export async function getVideoStatus(videoId: string) {
  const response = await fetchWithAuth(`videos/${videoId}`);
  return response.json();
}

/**
 * Get video gallery with pagination and filtering
 */
export async function getVideoGallery(options?: {
  page?: number;
  limit?: number;
  status?: 'pending' | 'processing' | 'completed' | 'failed';
}) {
  const params = new URLSearchParams();
  
  if (options?.page) params.append('page', options.page.toString());
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.status) params.append('status', options.status);
  
  const queryString = params.toString();
  const endpoint = queryString ? `videos/gallery?${queryString}` : 'videos/gallery';
  
  const response = await fetchWithAuth(endpoint);
  return response.json();
}

/**
 * Delete video by ID
 */
export async function deleteVideo(videoId: string) {
  const response = await fetchWithAuth(`videos/${videoId}`, {
    method: 'DELETE',
  });
  return response.json();
}

/**
 * Announcement Functions
 */

/**
 * Get active banner announcement (public endpoint)
 *
 * Goes through the /api/backend proxy. The proxy injects the server-only
 * X-API-Secret (required by the backend on every /api/* route), which a
 * browser cannot hold. `announcements/banner` is whitelisted in the proxy's
 * publicPaths so this works for logged-OUT visitors too (no auth_token cookie).
 */
export async function getBannerAnnouncement() {
  const response = await fetch('/api/backend/announcements/banner', {
    method: 'GET',
  });
  return response.json();
}

/**
 * Support Ticket Functions
 */

/**
 * Create a new support ticket
 */
export async function createSupportTicket(data: {
  subject: string;
  message: string;
  category: 'technical' | 'billing' | 'feature_request' | 'bug_report' | 'other';
  attachments?: string[];
}) {
  const response = await fetchWithAuth('support/tickets', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return response.json();
}

/**
 * Get user's support tickets
 */
export async function getSupportTickets(options?: {
  page?: number;
  limit?: number;
  status?: 'open' | 'in_progress' | 'waiting_user' | 'resolved' | 'closed';
}) {
  const params = new URLSearchParams();
  if (options?.page) params.append('page', options.page.toString());
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.status) params.append('status', options.status);
  
  const queryString = params.toString();
  const endpoint = queryString ? `support/tickets?${queryString}` : 'support/tickets';
  
  const response = await fetchWithAuth(endpoint);
  return response.json();
}

/**
 * Get single support ticket with replies
 */
export async function getSupportTicket(ticketId: string) {
  const response = await fetchWithAuth(`support/tickets/${ticketId}`);
  return response.json();
}

/**
 * Reply to a support ticket
 */
export async function replySupportTicket(ticketId: string, data: {
  message: string;
  attachments?: string[];
}) {
  const response = await fetchWithAuth(`support/tickets/${ticketId}/replies`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return response.json();
}

/**
 * Verify email with 6-digit code
 */
export async function verifyEmail(code: string) {
  const response = await fetchWithAuth('auth/verify-email', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });
  return response.json();
}

/**
 * Resend email verification code
 */
export async function resendVerificationCode() {
  const response = await fetchWithAuth('auth/resend-verification', {
    method: 'POST',
  });
  return response.json();
}

/**
 * Change the email on a still-unverified account (fixes a typo'd signup email),
 * then resend the verification code to the corrected address.
 */
export async function changeUnverifiedEmail(newEmail: string) {
  const response = await fetchWithAuth('auth/change-unverified-email', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ newEmail }),
  });
  return response.json();
}

/**
 * Check email verification status
 */
export async function getEmailVerificationStatus() {
  const response = await fetchWithAuth('auth/email-verification-status');
  return response.json();
}

/**
 * Verify email with 6-digit code (PUBLIC - before login)
 */
export async function verifyEmailPublic(userId: string, code: string) {
  const response = await fetch('/api/backend/auth/verify-email-public', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, code }),
  });
  return response.json();
}

/**
 * Resend email verification code (PUBLIC - before login)
 */
export async function resendVerificationCodePublic(userId: string) {
  const response = await fetch('/api/backend/auth/resend-verification-public', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId }),
  });
  return response.json();
}

/**
 * Submit feedback
 */
export async function submitFeedback(data: {
  type: 'bug' | 'feature' | 'improvement' | 'other';
  subject: string;
  message: string;
  rating?: number;
  metadata?: {
    url?: string;
    browser?: string;
    os?: string;
  };
}) {
  const response = await fetchWithAuth('feedback', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

/**
 * Get own feedback history
 */
export async function getFeedbackHistory(page = 1, limit = 20) {
  const response = await fetchWithAuth(`feedback?page=${page}&limit=${limit}`);
  return response.json();
}

/**
 * Get feature status (public endpoint, no auth required)
 * Returns which features are currently enabled/disabled by admin
 */
export async function getFeatureStatus() {
  const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.ultragoon.co';
  const response = await fetch(`${API_URL}/api/config/features`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response.json();
}

/**
 * Re-roll a video — same params + new seed.
 */
export async function regenerateVideo(videoId: string) {
  const response = await fetchWithAuth(`videos/${videoId}/regenerate`, { method: 'POST' });
  return response.json();
}

/**
 * Onboarding checklist progress + claim.
 */
export async function getOnboardingProgress() {
  const response = await fetchWithAuth('onboarding');
  return response.json();
}

export async function claimOnboardingTask(taskKey: string) {
  const response = await fetchWithAuth(`onboarding/claim/${taskKey}`, { method: 'POST' });
  return response.json();
}

/**
 * Web Push subscription — VAPID public key + register/unregister.
 */
export async function getPushPublicKey() {
  const response = await fetchWithAuth('push/public-key');
  return response.json();
}

export async function subscribePush(subscription: PushSubscriptionJSON) {
  const response = await fetchWithAuth('push/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subscription }),
  });
  return response.json();
}

export async function unsubscribePush(endpoint: string) {
  const response = await fetchWithAuth('push/unsubscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  });
  return response.json();
}

/**
 * User-facing notifications (bell icon).
 */
export async function getNotifications(page = 1, limit = 25, unreadOnly = false) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (unreadOnly) params.set('unreadOnly', '1');
  const response = await fetchWithAuth(`notifications?${params.toString()}`);
  return response.json();
}

export async function getNotificationUnreadCount() {
  const response = await fetchWithAuth('notifications/unread-count');
  return response.json();
}

export async function markNotificationRead(id: string) {
  const response = await fetchWithAuth(`notifications/${id}/read`, { method: 'POST' });
  return response.json();
}

export async function markAllNotificationsRead() {
  const response = await fetchWithAuth('notifications/read-all', { method: 'POST' });
  return response.json();
}

/**
 * Referral program — public code validation, used by /r/[code] landing page.
 * Returns { success: true, referrer: { displayName } } or { success: false }.
 */
export async function validateReferralCode(code: string) {
  const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.ultragoon.co';
  const response = await fetch(`${API_URL}/api/referrals/code/${encodeURIComponent(code)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) return { success: false as const };
  return response.json();
}

/**
 * Referral program — current user's stats for the Refer Friends dashboard.
 */
export async function getMyReferralStats() {
  const response = await fetchWithAuth('referrals/me');
  return response.json();
}

/**
 * Referral program — list of referees the current user has invited.
 */
export async function getMyReferralsList() {
  const response = await fetchWithAuth('referrals/me/list');
  return response.json();
}

/**
 * Check if an error is a "feature disabled" error
 * Returns true if the error is a 403 with "temporarily unavailable" message
 */
export function isFeatureDisabledError(error: any): boolean {
  // Check for fetch Response error
  if (error instanceof Response) {
    return error.status === 403;
  }
  
  // Check for error object with response property (axios-style)
  if (error?.response) {
    return (
      error.response.status === 403 &&
      error.response.data?.error === 'This feature is temporarily unavailable'
    );
  }
  
  // Check for parsed error with status
  if (error?.status === 403 && error?.error === 'This feature is temporarily unavailable') {
    return true;
  }
  
  return false;
}

/**
 * Blog/Updates Functions (Public endpoints)
 */

export interface BlogCategory {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  order: number;
}

export interface BlogPost {
  _id: string;
  title: string;
  slug: string;
  excerpt?: string;
  content: string;
  coverImage?: string;
  category: BlogCategory;
  categoryId?: BlogCategory; // For list responses
  author: {
    _id: string;
    email: string;
  };
  authorId?: {
    _id: string;
    email: string;
  };
  publishedAt: string;
  viewCount: number;
  seo?: {
    title: string;
    description: string;
    keywords: string[];
    ogImage: string;
    canonicalUrl: string | null;
  };
}

export interface PostListResponse {
  success: boolean;
  posts: BlogPost[];
  total: number;
  page: number;
  pages: number;
}

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://api.ultragoon.co';

/**
 * Headers for direct public backend calls made SERVER-SIDE (SSR/ISR).
 *
 * The backend gates every /api/* route behind X-API-Secret (only /config/features
 * and /errors/report are exempt). A browser must never hold this secret — it would
 * ship in the client bundle — so this returns {} on the client, where code must go
 * through the /api/backend proxy instead. Server components/route handlers can read
 * the server-only API_SECRET and attach it here, exactly like the proxy does.
 */
export function serverApiSecretHeaders(): Record<string, string> {
  if (typeof window !== 'undefined') return {};
  const secret = process.env.API_SECRET;
  return secret ? { 'X-API-Secret': secret } : {};
}

/**
 * Get paginated list of published blog posts
 */
export async function getBlogPosts(page = 1, limit = 10): Promise<PostListResponse> {
  const response = await fetch(
    `${API_BASE}/api/blog/posts?page=${page}&limit=${limit}`,
    { next: { revalidate: 60 }, headers: serverApiSecretHeaders() }
  );
  return response.json();
}

/**
 * Get a single blog post by slug
 */
export async function getBlogPost(slug: string): Promise<{ success: boolean; post: BlogPost }> {
  const response = await fetch(`${API_BASE}/api/blog/posts/${slug}`, {
    next: { revalidate: 60 },
    headers: serverApiSecretHeaders()
  });
  if (!response.ok) {
    throw new Error('Post not found');
  }
  return response.json();
}

/**
 * Get all blog categories
 */
export async function getBlogCategories(): Promise<{ success: boolean; categories: BlogCategory[] }> {
  const response = await fetch(`${API_BASE}/api/blog/categories`, {
    next: { revalidate: 300 },
    headers: serverApiSecretHeaders()
  });
  return response.json();
}

/**
 * Get posts by category slug
 */
export async function getCategoryPosts(
  categorySlug: string,
  page = 1,
  limit = 10
): Promise<PostListResponse> {
  const response = await fetch(
    `${API_BASE}/api/blog/categories/${categorySlug}/posts?page=${page}&limit=${limit}`,
    { next: { revalidate: 60 }, headers: serverApiSecretHeaders() }
  );
  return response.json();
}

/**
 * Social Rewards API Functions
 * Endpoints: GET /api/rewards, POST /api/rewards/:id/claim
 */

export interface SocialReward {
  id: string;
  platform: 'instagram' | 'twitter' | 'discord';
  displayName: string;
  accountHandle: string;
  accountUrl: string;
  creditsReward: number;
  instructions?: string;
  claimed: boolean;
  claimedAt?: string | null;
  userHandle?: string | null;
}

export interface ClaimResult {
  creditsAwarded: number;
  newBalance: number;
  message: string;
}

/**
 * Get all available social rewards with user's claim status
 * GET /api/rewards
 */
export async function getRewards(): Promise<{ success: boolean; data: { rewards: SocialReward[] } }> {
  const response = await fetchWithAuth('rewards');
  return response.json();
}

/**
 * Claim a social reward by providing the user's social handle
 * POST /api/rewards/:id/claim
 */
export async function claimReward(rewardId: string, userHandle: string): Promise<{ success: boolean; data?: ClaimResult; message?: string }> {
  const response = await fetchWithAuth(`rewards/${rewardId}/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userHandle }),
  });
  return response.json();
}

// ═══════════════════════════════════════
// Community
// ═══════════════════════════════════════

export async function getCommunityFeed(options?: { cursor?: string; limit?: number; sort?: string; tag?: string }) {
  const params = new URLSearchParams();
  if (options?.cursor) params.append('cursor', options.cursor);
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.sort) params.append('sort', options.sort);
  if (options?.tag) params.append('tag', options.tag);
  const response = await fetchWithAuth(`community/feed?${params.toString()}`);
  return response.json();
}

export async function getFollowingFeed(cursor?: string, limit?: number) {
  const params = new URLSearchParams();
  if (cursor) params.append('cursor', cursor);
  if (limit) params.append('limit', limit.toString());
  const response = await fetchWithAuth(`community/feed/following?${params.toString()}`);
  return response.json();
}

export async function getUserCommunityPosts(userId: string, cursor?: string) {
  const params = new URLSearchParams();
  if (cursor) params.append('cursor', cursor);
  const response = await fetchWithAuth(`community/feed/user/${userId}?${params.toString()}`);
  return response.json();
}

export async function createCommunityPost(data: { contentType: string; contentId: string; caption?: string; tags?: string[]; showPrompt?: boolean }) {
  const response = await fetchWithAuth('community/posts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function getCommunityPost(postId: string) {
  const response = await fetchWithAuth(`community/posts/${postId}`);
  return response.json();
}

export async function deleteCommunityPost(postId: string) {
  const response = await fetchWithAuth(`community/posts/${postId}`, { method: 'DELETE' });
  return response.json();
}

export async function togglePostLike(postId: string) {
  const response = await fetchWithAuth(`community/posts/${postId}/like`, { method: 'POST' });
  return response.json();
}

export async function getPostComments(postId: string, cursor?: string) {
  const params = cursor ? `?cursor=${cursor}` : '';
  const response = await fetchWithAuth(`community/posts/${postId}/comments${params}`);
  return response.json();
}

export async function addPostComment(postId: string, text: string, parentId?: string) {
  const response = await fetchWithAuth(`community/posts/${postId}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, parentId }),
  });
  return response.json();
}

export async function deletePostComment(commentId: string) {
  const response = await fetchWithAuth(`community/comments/${commentId}`, { method: 'DELETE' });
  return response.json();
}

export async function toggleFollowUser(userId: string) {
  const response = await fetchWithAuth(`community/users/${userId}/follow`, { method: 'POST' });
  return response.json();
}

export async function getSuggestedCreators(limit?: number) {
  const params = limit ? `?limit=${limit}` : '';
  const response = await fetchWithAuth(`community/users/suggested${params}`);
  return response.json();
}

export async function getUserFollowers(userId: string, cursor?: string) {
  const params = cursor ? `?cursor=${cursor}` : '';
  const response = await fetchWithAuth(`community/users/${userId}/followers${params}`);
  return response.json();
}

export async function getUserFollowing(userId: string, cursor?: string) {
  const params = cursor ? `?cursor=${cursor}` : '';
  const response = await fetchWithAuth(`community/users/${userId}/following${params}`);
  return response.json();
}

export async function getCommunityProfile(userId: string) {
  const response = await fetchWithAuth(`community/profile/${userId}`);
  return response.json();
}

export async function uploadAvatar(file: File) {
  const formData = new FormData();
  formData.append('avatar', file);
  const response = await fetchWithAuth('community/profile/avatar', {
    method: 'POST',
    body: formData,
  });
  return response.json();
}

export async function updateCommunityProfile(data: { bio?: string; website?: string }) {
  const response = await fetchWithAuth('community/profile', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

// ═══════════════════════════════════════
// Direct Messages
// ═══════════════════════════════════════

export async function getConversations(cursor?: string) {
  const params = cursor ? `?cursor=${cursor}` : '';
  const response = await fetchWithAuth(`community/conversations${params}`);
  return response.json();
}

export async function startConversation(userId: string) {
  const response = await fetchWithAuth('community/conversations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
  return response.json();
}

export async function getDMMessages(conversationId: string, cursor?: string) {
  const params = cursor ? `?cursor=${cursor}` : '';
  const response = await fetchWithAuth(`community/conversations/${conversationId}/messages${params}`);
  return response.json();
}

export async function sendDM(
  conversationId: string,
  text: string,
  sharedPostId?: string,
  replyToId?: string
) {
  const response = await fetchWithAuth(`community/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, sharedPostId, replyToId }),
  });
  return response.json();
}

/**
 * Connect Google to the current account (account settings). `credential` is the Google
 * ID token from the button. Throws (ApiError) on failure — e.g. email mismatch or the
 * Google account is already linked elsewhere.
 */
export async function linkGoogle(credential: string) {
  const response = await fetchWithAuth('auth/google/link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential }),
  });
  return response.json();
}

/** Disconnect Google from the current account. Throws if it would lock the user out. */
export async function unlinkGoogle() {
  const response = await fetchWithAuth('auth/google/unlink', { method: 'POST' });
  return response.json();
}

/** Toggle an emoji reaction on a DM message. Returns { messageId, reactions }. */
export async function reactToDM(conversationId: string, messageId: string, emoji: string) {
  const response = await fetchWithAuth(
    `community/conversations/${conversationId}/messages/${messageId}/react`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emoji }),
    }
  );
  return response.json();
}

export async function markDMRead(conversationId: string) {
  const response = await fetchWithAuth(`community/conversations/${conversationId}/read`, { method: 'POST' });
  return response.json();
}

export async function reportCommunityContent(targetType: string, targetId: string, reason: string, details?: string) {
  const response = await fetchWithAuth('community/report', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetType, targetId, reason, details }),
  });
  return response.json();
}

// ═══════════════════════════════════════
// Agent Chat (ChatGPT-style conversations) — REST under /api/agent
// Realtime turns + generation updates go over socket.io (see useAgentChat).
// ═══════════════════════════════════════

/** GET /api/agent/conversations — cursor-paginated list for the sidebar. */
export async function getAgentConversations(cursor?: string, limit?: number) {
  const params = new URLSearchParams();
  if (cursor) params.append('cursor', cursor);
  if (limit) params.append('limit', String(limit));
  const qs = params.toString();
  const response = await fetchWithAuth(`agent/conversations${qs ? `?${qs}` : ''}`);
  return response.json();
}

/** POST /api/agent/conversations — create an empty conversation (optional first text for title). */
export async function createAgentConversation(firstText?: string) {
  const response = await fetchWithAuth('agent/conversations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(firstText ? { firstText } : {}),
  });
  return response.json();
}

/** GET /api/agent/conversations/:id — conversation + first (ascending) page of messages. */
export async function getAgentConversation(id: string) {
  const response = await fetchWithAuth(`agent/conversations/${id}`);
  return response.json();
}

/** GET /api/agent/conversations/:id/messages — cursor-paginated messages (ascending). */
export async function getAgentMessages(id: string, cursor?: string, limit?: number) {
  const params = new URLSearchParams();
  if (cursor) params.append('cursor', cursor);
  if (limit) params.append('limit', String(limit));
  const qs = params.toString();
  const response = await fetchWithAuth(`agent/conversations/${id}/messages${qs ? `?${qs}` : ''}`);
  return response.json();
}

/** PATCH /api/agent/conversations/:id — rename. */
export async function renameAgentConversation(id: string, title: string) {
  const response = await fetchWithAuth(`agent/conversations/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
  return response.json();
}

/** DELETE /api/agent/conversations/:id — soft delete. */
export async function deleteAgentConversation(id: string) {
  const response = await fetchWithAuth(`agent/conversations/${id}`, { method: 'DELETE' });
  return response.json();
}

/** DELETE /api/agent/conversations — clear ALL of the user's chat history (soft delete). */
export async function deleteAllAgentConversations() {
  const response = await fetchWithAuth('agent/conversations', { method: 'DELETE' });
  return response.json();
}

/** Get video status by ID (alias for getVideoStatus, used by the generation hook). */
export async function getVideoById(videoId: string) {
  return getVideoStatus(videoId);
}
